import { createSlice } from "@reduxjs/toolkit";
import { STATUS } from "./OrganizationSlice";
import { BreadcrumbOrg } from "../../services/OrganizationsV3Service";
import {
  deactivateOrganizationAsync,
  fetchDeactivationPreviewAsync,
  fetchOrganizationAncestorsAsync,
  fetchOrganizationsV3Async,
  fetchOrganizationsV3StatsAsync,
  fetchUserOrganizationAsync,
  renameOrganizationAsync,
} from "../thunks/OrganizationsV3Thunk";

// Row shape of GET /master-data/v3/organizations. v3 is camelCase throughout —
// do not reuse the v1 Organization type from OrganizationSlice.
export interface OrganizationV3 {
  id: number;
  name: string;
  displayName: string;
  parentOrgId: number | null;
  status: string; // "ACTIVE" | "DEACTIVATED"
  statusDate: string | null; // ISO 8601; can be null on legacy rows
  activeUsers: number; // users directly in this org only (not the subtree)
  activeSubOrgs: number; // direct active children only (not the subtree)
}

// GET /master-data/v3/organizations/stats. The field names do not change between
// screen states: totalActiveSubOrgs is system-wide without parentId and
// recursive-below-the-org with it — the card labels change, not the fields.
export interface OrganizationsV3Stats {
  totalActiveSubOrgs: number;
  totalActiveUsers: number;
}

// GET /master-data/v3/organizations/{orgId}/deactivation-preview. userCount is the
// full cascade blast radius (invited/signed-up users and staff included), which is
// intentionally larger than the "active users" shown in the table.
export interface DeactivationPreview {
  organizationId: number;
  name: string;
  status: string;
  subOrgCount: number;
  userCount: number;
}

// POST /master-data/v3/organizations/{orgId}/deactivate response.
export interface DeactivateResult {
  organizationId: number;
  cascadeId: string | null;
  deactivatedOrganizations: number;
  deactivatedUsers: number;
  alreadyDeactivated: boolean;
}

// PATCH /master-data/v3/organizations/{orgId} response: the updated list row plus
// the name before the call and whether anything actually changed (false when the
// trimmed name equals the current one — nothing is written or audited).
export interface RenameResult extends OrganizationV3 {
  previousName: string;
  renamed: boolean;
}

interface OrganizationsV3State {
  organizations: OrganizationV3[];
  page: number;
  totalPages: number;
  totalItems: number;
  listStatus: string;
  listError: string | null;
  stats: OrganizationsV3Stats | null;
  statsStatus: string;
  // Name of the signed-in user's own organization, for the top-level title.
  userOrganizationName: string | null;
  // Breadcrumb ancestors (root-first) of `ancestorsOrgId`; empty at the top level.
  ancestors: BreadcrumbOrg[];
  ancestorsOrgId: number | null;
  ancestorsStatus: string;
  deactivationPreview: DeactivationPreview | null;
  previewStatus: string;
  previewError: string | null;
  deactivateStatus: string;
  deactivateError: string | null;
  renameStatus: string;
  renameError: string | null;
}

const initialState: OrganizationsV3State = {
  organizations: [],
  page: 1,
  totalPages: 0,
  totalItems: 0,
  listStatus: STATUS.IDLE,
  listError: null,
  stats: null,
  statsStatus: STATUS.IDLE,
  userOrganizationName: null,
  ancestors: [],
  ancestorsOrgId: null,
  ancestorsStatus: STATUS.IDLE,
  deactivationPreview: null,
  previewStatus: STATUS.IDLE,
  previewError: null,
  deactivateStatus: STATUS.IDLE,
  deactivateError: null,
  renameStatus: STATUS.IDLE,
  renameError: null,
};

const organizationsV3Slice = createSlice({
  name: "organizationsV3",
  initialState,
  reducers: {
    resetOrganizationsV3: () => initialState,
    // Called every time the deactivate dialog opens so stale preview counts or a
    // previous attempt's error never leak into a new confirmation.
    resetDeactivationState: (state) => {
      state.deactivationPreview = null;
      state.previewStatus = STATUS.IDLE;
      state.previewError = null;
      state.deactivateStatus = STATUS.IDLE;
      state.deactivateError = null;
    },
    // Called every time the rename dialog opens so a previous attempt's error
    // does not show for a different organization.
    resetRenameState: (state) => {
      state.renameStatus = STATUS.IDLE;
      state.renameError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationsV3Async.pending, (state) => {
        state.listStatus = STATUS.LOADING;
        state.listError = null;
      })
      .addCase(fetchOrganizationsV3Async.fulfilled, (state, action) => {
        state.listStatus = STATUS.SUCCESS;
        state.organizations = action.payload?.items ?? [];
        state.page = action.payload?.page ?? 1;
        state.totalPages = action.payload?.totalPages ?? 0;
        state.totalItems = action.payload?.totalItems ?? 0;
      })
      .addCase(fetchOrganizationsV3Async.rejected, (state, action) => {
        state.listStatus = STATUS.FAILED;
        state.listError = (action.payload as string) ?? "An unexpected error occurred";
        state.organizations = [];
      })

      .addCase(fetchOrganizationsV3StatsAsync.pending, (state) => {
        state.statsStatus = STATUS.LOADING;
      })
      .addCase(fetchOrganizationsV3StatsAsync.fulfilled, (state, action) => {
        state.statsStatus = STATUS.SUCCESS;
        state.stats = action.payload ?? null;
      })
      .addCase(fetchOrganizationsV3StatsAsync.rejected, (state) => {
        state.statsStatus = STATUS.FAILED;
        state.stats = null;
      })

      .addCase(fetchUserOrganizationAsync.fulfilled, (state, action) => {
        state.userOrganizationName = action.payload.name ?? null;
      })
      // Rejection is cosmetic: the title falls back to "Welcome back".

      .addCase(fetchOrganizationAncestorsAsync.pending, (state) => {
        state.ancestorsStatus = STATUS.LOADING;
      })
      .addCase(fetchOrganizationAncestorsAsync.fulfilled, (state, action) => {
        state.ancestorsStatus = STATUS.SUCCESS;
        state.ancestors = action.payload.ancestors;
        state.ancestorsOrgId = action.payload.orgId;
      })
      .addCase(fetchOrganizationAncestorsAsync.rejected, (state) => {
        // A missing trail is cosmetic; the header and back button still work.
        state.ancestorsStatus = STATUS.FAILED;
        state.ancestors = [];
        state.ancestorsOrgId = null;
      })

      .addCase(fetchDeactivationPreviewAsync.pending, (state) => {
        state.previewStatus = STATUS.LOADING;
        state.previewError = null;
      })
      .addCase(fetchDeactivationPreviewAsync.fulfilled, (state, action) => {
        state.previewStatus = STATUS.SUCCESS;
        state.deactivationPreview = action.payload ?? null;
      })
      .addCase(fetchDeactivationPreviewAsync.rejected, (state, action) => {
        state.previewStatus = STATUS.FAILED;
        state.previewError = (action.payload as string) ?? "An unexpected error occurred";
        state.deactivationPreview = null;
      })

      .addCase(deactivateOrganizationAsync.pending, (state) => {
        state.deactivateStatus = STATUS.LOADING;
        state.deactivateError = null;
      })
      .addCase(deactivateOrganizationAsync.fulfilled, (state) => {
        state.deactivateStatus = STATUS.SUCCESS;
      })
      .addCase(deactivateOrganizationAsync.rejected, (state, action) => {
        state.deactivateStatus = STATUS.FAILED;
        state.deactivateError = (action.payload as string) ?? "An unexpected error occurred";
      })

      .addCase(renameOrganizationAsync.pending, (state) => {
        state.renameStatus = STATUS.LOADING;
        state.renameError = null;
      })
      .addCase(renameOrganizationAsync.fulfilled, (state, action) => {
        state.renameStatus = STATUS.SUCCESS;
        // Swap the row in place so the list reflects the rename without a refetch.
        // The response is the updated V3 row (displayName may or may not follow
        // the name — the server decides), so prefer it over the submitted body.
        const { orgId, body } = action.meta.arg;
        const updated = action.payload as RenameResult | undefined;
        const index = state.organizations.findIndex((org) => org.id === orgId);
        if (index !== -1) {
          const current = state.organizations[index];
          state.organizations[index] = updated?.id
            ? { ...current, ...updated }
            : { ...current, name: body.name, displayName: body.name };
        }
      })
      .addCase(renameOrganizationAsync.rejected, (state, action) => {
        state.renameStatus = STATUS.FAILED;
        state.renameError = (action.payload as string) ?? "An unexpected error occurred";
      });
  },
});

export const { resetOrganizationsV3, resetDeactivationState, resetRenameState } =
  organizationsV3Slice.actions;
export default organizationsV3Slice.reducer;
